import { useTranslation } from "react-i18next";
import {
  IconBellRinging,
  IconClockHour4,
  IconRefresh,
} from "@tabler/icons-react";
import { isDesktopShell } from "../../../common/hooks/useIsDesktopView";
import { cn } from "../../../lib/utils";
import { buttonVariants } from "../../ui/Button";
import { useApiQuery } from "../../../services/api";
import { useCurrentUser } from "../../../domains/user/hooks/useCurrentUser";
import { useNotificationOptIn } from "../../../domains/notification/hooks/useNotificationOptIn";
import { useNotificationPermissionState } from "../../../domains/notification/hooks/useNotificationPermissionState";

/**
 * Everything a failed chat turn shows the user — and the ONE place that decides
 * whether the turn was REFUSED (out of credits) or genuinely broke.
 *
 * That decision comes from the server's typed budget state
 * (`/webapp/private/conversations/token-budget`), never from the error message
 * text: a refusal and a failure look identical to the AI SDK transport, and the
 * string-sniffing this replaces (`["daily","limit","reach"].some(...)`) both
 * missed the weekly ceiling and mislabelled unrelated errors. Retrying a refusal
 * cannot help, so the two states are mutually exclusive — hence one component
 * rather than a caller juggling both.
 *
 * The refusal arm is also the right moment to ask for push: the recovery
 * notification is what brings the user back.
 */
export default function GPTChatError({
  onRetry,
}: {
  onRetry: (() => void) | null;
}): React.ReactNode {
  const { t } = useTranslation();
  const { permissionState } = useNotificationPermissionState();
  // `onDone` is a no-op: this notice stays visible either way — the user's credits
  // are still spent, and dismissing it would just hide why the answer failed.
  const { isRequesting, showOpenSettings, enable, openSettings } =
    useNotificationOptIn(() => {});

  // Only a signed-in reader has a credit budget. The logged-out JuisciGPT page
  // renders this too, and a private read without a session is an error the
  // visitor did nothing to cause.
  const { isAuthenticated } = useCurrentUser();
  const budget = useApiQuery(
    "/webapp/private/conversations/token-budget",
    isAuthenticated
      ? {
          // The state changes only as spend ages out of the window; no need
          // to re-ask on every render of a chat that just failed.
          staleTime: 30_000,
        }
      : false
  );

  // Anything other than a confirmed refusal is a real error. While the budget
  // query is still in flight we show the generic message rather than nothing —
  // a silent failure is the worse of the two guesses.
  // On desktop, the card of Figma "D2.conversation.erreur" ("answer · error");
  // the phone keeps its line and its Retry pill.
  if (
    (budget.state !== "success" || budget.body.state === "ok") &&
    !isDesktopShell()
  ) {
    return (
      <div className="mx-5 flex flex-col items-start gap-2">
        <p className="my-0 text-[14px] font-normal leading-[26px] text-secondary-raspberry-1">
          {t("❌ Sorry an error has occurred. Please retry.")}
        </p>
        {onRetry !== null && (
          <button
            className="flex items-center gap-2 rounded-full bg-white px-4 py-2 text-[14px] font-medium text-ds-mint-800 border-none cursor-pointer shadow-[0px_2px_8px_rgba(0,0,0,0.08)]"
            onClick={onRetry}
          >
            <IconRefresh size={18} stroke={1.5} />
            {t("Retry")}
          </button>
        )}
      </div>
    );
  }
  if (budget.state !== "success" || budget.body.state === "ok") {
    return (
      <div className="mx-5 flex flex-col items-start gap-2 rounded-xl bg-ds-strawberry-50 p-4 font-dm-sans text-sm">
        <p className="m-0 font-semibold text-ds-strawberry-700">
          {t("The answer couldn't be generated")}
        </p>
        <p className="m-0 text-ds-strawberry-900">
          {t(
            "Something went wrong on our side. Your question stays in the conversation, you can try again."
          )}
        </p>
        {onRetry !== null && (
          <div className="pt-1">
            <button
              type="button"
              className={cn(
                buttonVariants({ variant: "destructive", size: "sm" }),
                "border-ds-cream/32 px-4 [--ds-btn-base:var(--color-ds-strawberry-900)]"
              )}
              onClick={onRetry}
            >
              {t("Try again")}
            </button>
          </div>
        )}
      </div>
    );
  }

  // Two refusal states, two messages: today's credits are gone (back overnight)
  // or this week's are (back next week). Nothing else can refuse a turn — the
  // per-day TURN counter that used to be a third state is gone; credits are the
  // single ceiling now.
  const isWeekly = budget.body.state === "weekly_exhausted";
  const recoversAt =
    budget.body.recoversAt === null ? null : new Date(budget.body.recoversAt);

  // "Tomorrow" / "next week" is the shape the product asked for; the exact
  // recovery instant backs it up so a user who reads it literally isn't misled
  // (a rolling window frees up at a real time, not at midnight).
  const whenLabel =
    recoversAt === null
      ? null
      : recoversAt.toLocaleString(undefined, {
          weekday: "long",
          hour: "2-digit",
          minute: "2-digit",
        });

  // "authorized", not "granted" — the app's own status union (it also covers the
  // native shells, where the OS answer isn't the web Permission API's).
  const granted =
    permissionState !== null && permissionState.status === "authorized";

  return (
    <div className="mx-5 flex flex-col items-start gap-3 rounded-2xl bg-white p-4 shadow-[0px_2px_8px_rgba(0,0,0,0.08)]">
      <div className="flex items-center gap-2">
        <IconClockHour4 size={20} stroke={1.5} className="text-ds-mint-800" />
        <p className="my-0 text-[15px] font-semibold leading-[22px]">
          {isWeekly
            ? t("You've used all your Juisci GPT credits for this week")
            : t("You've used all your Juisci GPT credits for today")}
        </p>
      </div>

      <p className="my-0 text-[14px] font-normal leading-[22px] text-grey-08">
        {isWeekly
          ? t(
              "Your credits refill next week. Come back then to continue this conversation."
            )
          : t(
              "Your credits refill tomorrow. Come back then to continue this conversation."
            )}
        {whenLabel === null
          ? null
          : ` ${t("You can ask again from {{when}}.", { when: whenLabel })}`}
      </p>

      {granted ? (
        <p className="my-0 flex items-center gap-2 text-[14px] font-normal leading-[22px] text-ds-mint-800">
          <IconBellRinging size={18} stroke={1.5} />
          {t("We'll notify you as soon as your credits are back.")}
        </p>
      ) : showOpenSettings ? (
        // Permission was refused at the OS level: the app can no longer prompt,
        // so the only honest instruction is "turn it on in settings".
        <div className="flex flex-col items-start gap-2">
          <p className="my-0 text-[14px] font-normal leading-[22px] text-grey-08">
            {t(
              "Notifications are turned off. Enable them in your app settings to be told when your credits are back."
            )}
          </p>
          <button
            className="flex items-center gap-2 rounded-full bg-white px-4 py-2 text-[14px] font-medium text-ds-mint-800 border border-solid border-ds-mint-800 cursor-pointer"
            onClick={openSettings}
          >
            <IconBellRinging size={18} stroke={1.5} />
            {t("Open settings")}
          </button>
        </div>
      ) : (
        <div className="flex flex-col items-start gap-2">
          <p className="my-0 text-[14px] font-normal leading-[22px] text-grey-08">
            {t(
              "Turn on notifications and we'll tell you when you can continue."
            )}
          </p>
          <button
            className="flex items-center gap-2 rounded-full bg-ds-mint-800 px-4 py-2 text-[14px] font-medium text-white border-none cursor-pointer disabled:opacity-60"
            disabled={isRequesting}
            onClick={enable}
          >
            <IconBellRinging size={18} stroke={1.5} />
            {isRequesting
              ? t("Enabling…")
              : t("Notify me when credits are back")}
          </button>
        </div>
      )}
    </div>
  );
}
